/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { setScheduledTask } from "../../../../store/api/tasks/reducer";
import { setErrorMsg, setOpenToast } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import AutocompleteSelect from "../../../Shared/Dropdown/AutocompleteSelect";
import { IOption } from "../../../Shared/Filter/types";
import { getNewOptions } from "../../helpers";
import { Link } from "react-router-dom";
import { useCustomAuditReport } from "./hooks";
import { RadixSelectControl } from "../../../ui/radix-select";
import { Label } from "../../../ui/label";
import { auditReportTypeValues } from "../../../../globals/constants";
import { useUploadCustomAuditTemplateMutation } from "../../../../store/api/tasks/taskApiSlice";
import { Button } from "../../../ui/button";
import { Info, Loader2, X } from "lucide-react";
import {
  CUSTOM_AUDIT_XML_ONLY_MESSAGE,
  extractCustomAuditUploadErrorMessage,
  resolveCustomAuditTemplateFileType,
} from "../../customAuditTemplateUploadUtils";
import CustomAuditWildcardTooltip from "../../CustomAuditWildcardTooltip";
import FileUpload from "../../../ui/file-upload";

const NODE_TYPE_OPTIONS = [
  { value: "ALL", label: "ALL" },
  { value: "LTE", label: "LTE" },
  { value: "NR", label: "NR" },
];

const MANUAL_ATTRIBUTE_MAX_LINES = 10;
const MANUAL_ATTRIBUTE_MAX_COUNT = 10;

const countManualAttributes = (rawInput: string): number => {
  if (!rawInput || !rawInput.trim()) {
    return 0;
  }

  return rawInput
    .split(/[\r\n,]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0).length;
};

const CustomAuditReport = () => {
  const dispatch = useStoreDispatch();
  const [uploadCustomAuditTemplate, { reset: resetUploadMutation }] = useUploadCustomAuditTemplateMutation();
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(null);
  const [uploadDescription, setUploadDescription] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const {
    errorMsg,
    scheduledtasks,
    emailGroupNameOptions = [],
    emailGroupName,
    setEmailGroupName,
    templateOptions,
    refetchTemplates,
  } = useCustomAuditReport();

  const inputMode = (scheduledtasks.customReportInputMode || "TEMPLATE") as "TEMPLATE" | "MANUAL" | "UPLOAD";
  const templateNamesSet = useMemo(() => new Set(templateOptions.map((option) => option.value)), [templateOptions]);

  // Set the report type for this task on mount — keeps it out of the common payload helper
  useEffect(() => {
    dispatch(setScheduledTask({ auditReportType: auditReportTypeValues.customAuditReport }));
  }, [dispatch]);

  useEffect(() => {
    if (scheduledtasks.reportFileName === undefined) {
      dispatch(setScheduledTask({ reportFileName: "" }));
    }
  }, [dispatch, scheduledtasks.reportFileName]);

  useEffect(() => {
    if (scheduledtasks.nodeType === undefined) {
      dispatch(setScheduledTask({ nodeType: "ALL" }));
    }
  }, [dispatch, scheduledtasks.nodeType]);

  const onCustomInputModeChange = (value: string) => {
    dispatch(
      setScheduledTask({
        customReportInputMode: value as "TEMPLATE" | "MANUAL" | "UPLOAD",
        ...(value === "MANUAL" ? { period: "0" } : {}),
        templateName: "",
        manualAttributes: "",
        nodeType: "ALL",
      })
    );
    setSelectedUploadFile(null);
    setUploadDescription("");
    dispatch(setErrorMsg({ ...errorMsg, templateName: "", manualAttributes: "" }));
  };

  const onTemplateChange = (value: string) => {
    dispatch(setScheduledTask({ templateName: value === "__placeholder__" ? "" : value }));
    dispatch(setErrorMsg({ ...errorMsg, templateName: "", manualAttributes: "" }));
  };

  const onManualAttributesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const updatedValue = event.target.value;
    const lines = updatedValue.split(/\r?\n/);
    if (lines.length > MANUAL_ATTRIBUTE_MAX_LINES) {
      const cappedValue = lines.slice(0, MANUAL_ATTRIBUTE_MAX_LINES).join("\n");
      dispatch(setScheduledTask({ manualAttributes: cappedValue }));
      dispatch(
        setErrorMsg({
          ...errorMsg,
          manualAttributes: "Manual attributes are limited to 10 lines.",
          templateName: "",
        })
      );
      return;
    }

    const attributeCount = countManualAttributes(updatedValue);
    if (attributeCount > MANUAL_ATTRIBUTE_MAX_COUNT) {
      dispatch(setScheduledTask({ manualAttributes: updatedValue }));
      dispatch(
        setErrorMsg({
          ...errorMsg,
          manualAttributes: "Manual attributes are limited to 10 attributes.",
          templateName: "",
        })
      );
      return;
    }

    dispatch(setScheduledTask({ manualAttributes: updatedValue }));
    dispatch(setErrorMsg({ ...errorMsg, manualAttributes: "", templateName: "" }));
  };

  const onNodeTypeChange = (value: string) => {
    dispatch(setScheduledTask({ nodeType: value === "__placeholder__" ? "ALL" : value }));
  };

  const onUploadFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedUploadFile(event.target.files[0]);
    }
  };

  const clearSelectedUploadFile = () => {
    setSelectedUploadFile(null);
    if (uploadInputRef.current) {
      uploadInputRef.current.value = "";
    }
  };

  const onUploadTemplate = async () => {
    if (!selectedUploadFile) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: "Please select an XML file." }));
      return;
    }

    const fileType = resolveCustomAuditTemplateFileType(selectedUploadFile);
    if (fileType === null) {
      clearSelectedUploadFile();
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: CUSTOM_AUDIT_XML_ONLY_MESSAGE }));
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("file", selectedUploadFile);
      const response: any = await uploadCustomAuditTemplate({
        formData,
        fileType,
        description: uploadDescription,
      }).unwrap();

      const refetchResult: any = await refetchTemplates();
      const fetchedTemplates = (refetchResult?.data || []).map((item: any) => item.templateName || "").filter((name: string) => !!name);
      const uploadedTemplateName = response?.templateName || fetchedTemplates.find((name: string) => !templateNamesSet.has(name));
      const templateNameForToast = uploadedTemplateName || selectedUploadFile.name;

      if (uploadedTemplateName) {
        dispatch(
          setScheduledTask({
            templateName: uploadedTemplateName,
            customReportInputMode: "TEMPLATE",
            nodeType: "ALL",
          })
        );
      }

      dispatch(setErrorMsg({ ...errorMsg, templateName: "", manualAttributes: "" }));
      dispatch(setOpenToast({ openToast: true, toastType: "success", message: `Template "${templateNameForToast}" uploaded successfully` }));
      setSelectedUploadFile(null);
      setUploadDescription("");
      if (uploadInputRef.current) {
        uploadInputRef.current.value = "";
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: extractCustomAuditUploadErrorMessage(error) }));
      setSelectedUploadFile(null);
      if (uploadInputRef.current) {
        uploadInputRef.current.value = "";
      }
      resetUploadMutation();
    } finally {
      resetUploadMutation();
      setIsUploading(false);
    }
  };

  const autocompleteSelectHandler = (event: any, newValue: IOption[] | null) => {
    setEmailGroupName(newValue || []);
    dispatch(setScheduledTask({ emailGroupName: newValue?.map((item) => item.value).toString() }));
  };

  const noOption = (
    <p>
      {`No email group available. `}
      <Link className="nooption-link" to={"/administration/email"}>
        "Click here"
      </Link>
      {` to add an email group.`}
    </p>
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Input Mode: Template or Manual */}
      <div className="space-y-2">
        <Label>Input Mode</Label>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="custom-report-input-mode"
              checked={inputMode === "TEMPLATE"}
              onChange={() => onCustomInputModeChange("TEMPLATE")}
              className="h-4 w-4"
            />
            Select Template
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="custom-report-input-mode"
              checked={inputMode === "UPLOAD"}
              onChange={() => onCustomInputModeChange("UPLOAD")}
              className="h-4 w-4"
            />
            Upload Template
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="custom-report-input-mode"
              checked={inputMode === "MANUAL"}
              onChange={() => onCustomInputModeChange("MANUAL")}
              className="h-4 w-4"
            />
            Enter Attributes Manually
          </label>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Report File Name (optional)</Label>
        <input
          value={scheduledtasks.reportFileName || ""}
          onChange={(event) => dispatch(setScheduledTask({ reportFileName: event.target.value }))}
          maxLength={200}
          className="flex w-full rounded-md border border-input bg-transparent text-foreground px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          placeholder="Defaults to task name and timestamp"
        />
      </div>

      {inputMode === "MANUAL" && (
        <div className="space-y-2">
          <Label>Node Type</Label>
          <RadixSelectControl
            options={NODE_TYPE_OPTIONS}
            value={scheduledtasks.nodeType || "ALL"}
            placeholder="Node Type"
            onValueChange={onNodeTypeChange}
          />
        </div>
      )}

      {/* Template selector or manual attributes textarea */}
      {inputMode === "TEMPLATE" ? (
        <div className="space-y-2">
          <Label>Custom Report Template</Label>
          <RadixSelectControl
            options={[
              { value: "__placeholder__", label: "Select template" },
              ...templateOptions.map((item) => ({ value: item.value, label: item.title })),
            ]}
            value={scheduledtasks.templateName || "__placeholder__"}
            placeholder="Select template"
            onValueChange={onTemplateChange}
          />
        </div>
      ) : inputMode === "UPLOAD" ? (
        <div className="space-y-4 rounded-md border border-border p-4 bg-muted/20">

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Label>Upload a Template</Label>
              <CustomAuditWildcardTooltip>
                <button
                  type="button"
                  aria-label="Supported wildcards for custom audit templates"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-border/70 bg-background text-muted-foreground shadow-sm transition-all hover:border-primary/40 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
                >
                  <Info className="h-4 w-4" />
                </button>
              </CustomAuditWildcardTooltip>
            </div>
            <FileUpload
              file={selectedUploadFile}
              setFile={setSelectedUploadFile}
              accept=".xml,application/xml,text/xml"
              allowedTypes={["text/xml", "application/xml"]}
              maxSizeMB={10}
            />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <textarea
              value={uploadDescription}
              onChange={(event) => setUploadDescription(event.target.value)}
              rows={3}
              maxLength={500}
              className="flex w-full rounded-md border border-input bg-transparent text-foreground px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-y"
              placeholder="Add a brief description for this template"
            />
          </div>
          <div className="pt-1">
            <Button onClick={onUploadTemplate} disabled={!selectedUploadFile || isUploading}>
              {isUploading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Upload and Use Template
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Upload an XML template. After successful upload, the template is registered under Custom Audit Templates and selected automatically.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label>Manual Attributes</Label>
            <CustomAuditWildcardTooltip>
              <button
                type="button"
                aria-label="Supported wildcards for manual custom audit attributes"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-border/70 bg-background text-muted-foreground shadow-sm transition-all hover:border-primary/40 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
              >
                <Info className="h-4 w-4" />
              </button>
            </CustomAuditWildcardTooltip>
          </div>
          <textarea
            value={scheduledtasks.manualAttributes || ""}
            onChange={onManualAttributesChange}
            rows={6}
            className="flex w-full rounded-md border border-input bg-transparent text-foreground px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-y"
            placeholder="Enter attributes one per line or comma-separated"
          />
          {errorMsg?.manualAttributes && <p className="text-xs text-destructive">{errorMsg.manualAttributes}</p>}
          <p className="text-xs text-muted-foreground">
            Use one attribute per line, or separate multiple attributes with commas. Maximum 10 attributes (and maximum 10 lines). Manual mode is on-demand only and does not support scheduling.
          </p>
        </div>
      )}

      {/* Email Group */}
      <div className="space-y-2">
        <Label>Email Group Name</Label>
        <AutocompleteSelect
          name="emailGroupName"
          value={emailGroupName}
          multiple={true}
          id="emailGroupName"
          options={getNewOptions(emailGroupNameOptions, emailGroupName)}
          label={""}
          size={"small"}
          noOptionsText={noOption}
          onChange={autocompleteSelectHandler}
          disablePortal
        />
      </div>
    </div>
  );
};

export default CustomAuditReport;
